import './IntegrationAutoSetupPopup.css';
import { useState, useRef, useEffect } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { useParams } from 'react-router-dom';
import {
  useZendeskChatIntegrationAutoSetupMutation,
  useZendeskEmailIntegrationAutoSetupMutation,
} from 'src/services';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { AutoSetupIcon } from 'src/assets/icons/autoSetupIcon';
import { IntegrationZendeskEmailAutoSetup } from './IntegrationZendeskEmailAutoSetup/IntegrationZendeskEmailAutoSetup';
import { IntegrationZendeskChatAutoSetup } from 'src/components/Integrations/IntegrationAutoSetupPopup/IntegrationZendeskChatAutoSetup/IntegrationZendeskChatAutoSetup';
import { Button } from 'src/components/ui/button';
import type { ProjectIntegration } from 'src/services/projectsService';
import type { ZendeskEmailAutoSetupData } from './IntegrationZendeskEmailAutoSetup/IntegrationZendeskEmailAutoSetup';
import type { ZendeskChatAutoSetupData } from 'src/components/Integrations/IntegrationAutoSetupPopup/IntegrationZendeskChatAutoSetup/IntegrationZendeskChatAutoSetup';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const IntegrationAutoSetupPopup = ({
  integration,
  disabled = false,
  isOpen: _isOpen = false,
}: {
  integration: ProjectIntegration;
  disabled?: boolean;
  isOpen?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const notification = useNotification();
  const { id } = useParams();
  const [integrationType] = useState(integration.integration_type);
  const popupContentRef = useRef();

  const [
    setZendeskEmailAutoSetup,
    { isSuccess: isSetZendeskEmailAutoSetup, isLoading: isLoadingSetZendeskEmailAutoSetup },
  ] = useZendeskEmailIntegrationAutoSetupMutation();

  const [
    setZendeskChatAutoSetup,
    { isSuccess: isSetZendeskChatAutoSetup, isLoading: isLoadingSetZendeskChatAutoSetup },
  ] = useZendeskChatIntegrationAutoSetupMutation();

  const submitEmail = async (formData: ZendeskEmailAutoSetupData) => {
    setZendeskEmailAutoSetup({
      projectId: id,
      integrationId: formData.integrationId,
      body: formData,
    });
  };
  const submitChat = async (formData: ZendeskChatAutoSetupData) => {
    setZendeskChatAutoSetup({
      projectId: id,
      integrationId: formData.integrationId,
      body: formData,
    });
  };

  useEffect(() => {
    if (isSetZendeskEmailAutoSetup || isSetZendeskChatAutoSetup) {
      notification.success('Integration Updated.');
      setOpen(false);
    }
  }, [isSetZendeskEmailAutoSetup, isSetZendeskChatAutoSetup]);

  useLoaderWhile(isLoadingSetZendeskEmailAutoSetup, isLoadingSetZendeskChatAutoSetup);

  const renderForm = () => {
    switch (integrationType) {
      case 'zendesk_chat':
        return (
          <IntegrationZendeskChatAutoSetup
            integration={integration}
            onSubmit={(formData) => submitChat(formData)}
          />
        );

      case 'zendesk_email':
        return (
          <IntegrationZendeskEmailAutoSetup
            integration={integration}
            onSubmit={(formData) => submitEmail(formData)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        <div className="edit-button [&_svg]:hover:opacity-100 [&_svg]:opacity-40 [&_svg]:transition [&_svg>path]:transition duration-300">
          <AutoSetupIcon />
        </div>
      </DialogTrigger>
      <DialogContent className={`integration-popup ${integrationType}`}>
        <DialogHeader>
          <DialogTitle>Auto setup settings</DialogTitle>
        </DialogHeader>
        <div className="content" ref={popupContentRef}>
          {integrationType && renderForm()}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="pill-outline-dark" size="pill" style={{ width: 'auto' }}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="pill-blue"
            size="pill"
            style={{ width: 'auto' }}
            type="submit"
            form="auto-setup-form"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
